import * as React from "react";
import { FunctionComponent, useEffect, CSSProperties, useState } from "react";
import {
  ColorScheme,
  StoreState,
  BaseModuleProps,
  EasiPayModuleSettings,
  WindowState,
} from "../../types";
import { connect, ConnectedProps, MapStateToProps } from "react-redux";
import { getActiveColorScheme } from "../../utils/utils";
import { getActiveSite } from "../../selectors/sites";
import { injectScript } from "../../actions/LoadStates";
import * as ClassNames from "classnames";
import ModuleHeadings from "../ModuleHeadings";
import ModuleWithHeadings from "../ModuleWithHeadings";
import LazyloadWrapper from "../LazyloadWrapper";

interface Props extends BaseModuleProps<EasiPayModuleSettings> {}

interface StateProps {
  scheme: ColorScheme;
}

type ReduxProps = ConnectedProps<typeof connector>;

const EasiPayModule: FunctionComponent<Props & ReduxProps> = ({
  scheme,
  translatedModule: {
    id,
    settings: { authId, textAlign },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  injectScript,
  isActive,
  isPreview,
  isFirstOnPage,
}) => {
  const [isLazyloaded, setIsLazyloaded] = useState(false);

  const style: CSSProperties | undefined =
    !isActive && isPreview
      ? {
          pointerEvents: "none",
        }
      : undefined;

  useEffect(() => {
    authId &&
      isLazyloaded &&
      injectScript("https://www.easymailing.eu/frontend/easiplugin.php").then(
        () => {
          const windowState = (window as unknown) as WindowState;
          windowState.loadframe?.();
        }
      );
  }, [isLazyloaded, isPreview, authId]);

  if (!isPreview && !authId) return null;

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      className="EasiPayModule"
      style={{
        background: scheme.main.background,
        color: scheme.main.text,
      }}
    >
      <div className="Module__Wrapper">
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>

      <LazyloadWrapper onLoad={setIsLazyloaded}>
        <div
          className={ClassNames("Module__Wrapper EasiPayModule__Content", {
            "EasiPayModule__Content--empty": !authId,
          })}
        >
          <div
            key={authId}
            id="easiplugin_container"
            data-module="easipay"
            data-lang={languageId}
            data-key={authId}
            style={style}
          />
        </div>
      </LazyloadWrapper>
    </ModuleWithHeadings>
  );
};

const mapStateToProps: MapStateToProps<StateProps, Props, StoreState> = (
  { colorSchemes, sites },
  { translatedModule }
): StateProps => ({
  scheme: getActiveColorScheme(
    colorSchemes,
    getActiveSite(sites),
    translatedModule
  ),
});

const mapDispatchToProps = {
  injectScript,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(EasiPayModule);
